/**
 * Che cosa mostrare nella schermata degli aggiornamenti.
 *
 * Tenuto fuori dal componente perché le situazioni si escludono a vicenda e
 * l'ordine con cui si guardano **conta**: «in corso» vince su «disponibile»,
 * «bloccata» vince su tutto il resto. Dentro il JSX diventerebbe una catena di
 * ternari, e la regola che il pulsante non deve comparire sulle versioni
 * bloccate finirebbe per dipendere da dove qualcuno ha messo una parentesi.
 */
import type { FaseAvviso } from '../../componenti/avvisoFermo.ts';
import type { StatoAggiornamenti } from '../../api/tipi-dati.ts';

export type TipoSituazione =
  | 'spento'
  | 'inCorso'
  | 'inPreavviso'
  | 'bloccata'
  | 'disponibile'
  | 'aggiornata';

export interface Situazione {
  tipo: TipoSituazione;
  /** Se il pulsante «Installa ora» va mostrato. */
  pulsante: boolean;
  titolo: string;
  spiegazione: string;
  /** Se sotto va raccontato l'ultimo tentativo andato male. */
  raccontaUltimoTentativo: boolean;
}

/**
 * `fase` è quella della striscia di preavviso, e serve a distinguere due
 * momenti che lo stato sul disco confonde in un solo `aggiornamentoInCorso`.
 *
 * ⚠️ Senza, chi sceglieva «fra 15 minuti» leggeva «il server sta costruendo la
 * versione nuova: ci vogliono alcuni minuti» **per tutti i quindici**, mentre
 * il server non stava costruendo un bel niente: stava aspettando, apposta,
 * quello che gli era stato chiesto di aspettare. Non è la bugia grave che
 * abbiamo già corretto, ma è della stessa specie — la schermata dichiarava un
 * passo preciso sapendo solo che c'era qualcosa in corso.
 *
 * Il dato arriva dal contesto della striscia e non da una rotta nuova: quella
 * risposta il client ce l'ha già, e chiederla due volte al server sarebbe
 * lavoro in più per sapere una cosa già saputa.
 */
export function decidiCosaMostrare(stato: StatoAggiornamenti, fase: FaseAvviso): Situazione {
  const fallito = stato.ultimoTentativo?.esito === 'fallito';

  if (!stato.sistemaAttivo) {
    return {
      tipo: 'spento',
      pulsante: false,
      titolo: 'Aggiornamenti automatici non attivi',
      spiegazione:
        'Su questo server non è installato il sistema di aggiornamento. È normale sulla ' +
        'macchina di sviluppo: gli aggiornamenti automatici esistono solo in produzione.',
      raccontaUltimoTentativo: false,
    };
  }

  // Prima di «in corso»: durante il preavviso sono vere entrambe, e questa è
  // la più precisa delle due.
  if (stato.aggiornamentoInCorso && (fase === 'attesa' || fase === 'ultimoMinuto')) {
    return {
      tipo: 'inPreavviso',
      pulsante: false,
      titolo: 'Aggiornamento annunciato',
      spiegazione:
        'Il server sta aspettando il preavviso che è stato scelto, per dare tempo a chi ' +
        'sta lavorando di salvare. Il conto alla rovescia è nella striscia in cima. Allo ' +
        "scadere costruisce la versione nuova, e solo alla fine l'applicazione si ferma " +
        "per pochi secondi. L'esito arriva su Telegram.",
      raccontaUltimoTentativo: false,
    };
  }

  if (stato.aggiornamentoInCorso) {
    return {
      tipo: 'inCorso',
      pulsante: false,
      titolo: 'Aggiornamento in corso',
      spiegazione:
        'Il server sta costruendo la versione nuova: ci vogliono alcuni minuti, poi ' +
        "l'applicazione si ferma per pochi secondi soltanto. L'esito arriva su Telegram.",
      raccontaUltimoTentativo: false,
    };
  }

  if (stato.versioneDisponibileBloccata) {
    return {
      tipo: 'bloccata',
      pulsante: false,
      titolo: `La versione ${stato.versioneDisponibile ?? ''} non viene installata`,
      spiegazione:
        'È già stata provata ed è fallita, quindi non viene ritentata da sola. ' +
        'Si riparte rilasciando una versione nuova.',
      raccontaUltimoTentativo: true,
    };
  }

  if (stato.aggiornamentoDisponibile) {
    return {
      tipo: 'disponibile',
      pulsante: true,
      titolo: `Disponibile la versione ${stato.versioneDisponibile ?? ''}`,
      spiegazione:
        "Verrà installata da sé stanotte. Puoi installarla subito: l'applicazione resta " +
        'irraggiungibile solo pochi secondi, alla fine — i minuti li richiede la ' +
        'costruzione della versione nuova, prima di toccare qualsiasi cosa. Chi sta ' +
        'compilando un modulo vede un conto alla rovescia e può salvare quello che sta ' +
        'scrivendo.',
      raccontaUltimoTentativo: fallito,
    };
  }

  return {
    tipo: 'aggiornata',
    pulsante: false,
    titolo: 'Versione aggiornata',
    spiegazione: 'Non ci sono versioni nuove da installare.',
    raccontaUltimoTentativo: fallito,
  };
}
